import { Lib } from "albertine-shared-web";
import type React from "react";
import { useHistory } from "react-router";
import { defaultRequestTitle, type Request } from "../../common/types/Request";
import { Flex } from "../Common/Flex";
import "./RequestSnippet.css";
import { OfferingCategory } from "@albertine/types";
import { useAgentsMap } from "../../common/hooks/useAgents";
import { useMembersMap } from "../../common/hooks/useMembers";
import type { Booking } from "../../common/types/Booking";
import { DeadlineStepToTextMap } from "../../common/types/Deadline";
import { categoryDisplayName } from "../../common/types/OfferingCategory";
import { requestIsActionable } from "../../common/utils/requests.util";
import { singularPlural } from "../../common/utils/time.formatted.util";
import { isTypeOf } from "../../common/utils/zod.util";
import { emptyImage } from "../../utils/file.util";
import { formatRequestDateTimeStrings } from "../../utils/time.util";
import { fromTimestampToDate } from "../../utils/timestamp.util";
import { isMobile } from "../../utils/window.util";
import { AgentAvatarWithSecondary } from "../Common/Avatar";
import Button from "../Common/Buttons";
import DeadlineTag from "../Common/DeadlineTag";
import Icon from "../Common/Icon";
import Label from "../Common/Label";
import Stars from "../Common/rating/Stars";
import Tag from "../Common/Tag";
import TimeTag from "../Common/TimeTag";

type RequestSnippetWrapperProps = {
    children: React.ReactNode;
};

type SnippetProps = {
    request: Request;
};
type ConfirmedSnippetProps = SnippetProps & {
    confirmedBookings: Booking[];
};
type RequestSnippetProps = SnippetProps & {
    confirmedBookings: {
        request: string;
        bookings: Booking[];
    }[];
};

function Divider() {
    return <hr className="request-snippet__divider" />;
}

function RequestTitle(props: SnippetProps) {
    const { request } = props;
    return <h5>{request.title || defaultRequestTitle}</h5>;
}

function RequestDetails(props: SnippetProps) {
    const { request } = props;
    const requestDateFormatted = formatRequestDateTimeStrings(
        request.startDate,
        request.startTime,
        request.endDate,
    );

    const requestDateRange = requestDateFormatted || "Date missing";

    const requestNumberOfQuests = request.numberOfGuests
        ? singularPlural(
              `${request.numberOfGuests} guest`,
              `${request.numberOfGuests} guests`,
              request.numberOfGuests,
          )
        : undefined;
    const requestCategory =
        request.offeringCategory && isTypeOf(request.offeringCategory, OfferingCategory)
            ? categoryDisplayName(request.offeringCategory, "en")
            : undefined;

    const details = Lib.Utils.Strings.combineWith(
        [requestDateRange, requestNumberOfQuests, requestCategory],

        " · ",
    );

    return details ? <Label.Small.Normal.N50>{details}</Label.Small.Normal.N50> : null;
}

function RequestBookingImage(props: { bookings: Booking[] }) {
    const { bookings } = props;
    const firstConfirmedBooking = bookings[0];
    return firstConfirmedBooking ? (
        <div className="request-snippet__confimed-booking">
            <img
                className="request-snippet__confimed-booking-image"
                src={
                    firstConfirmedBooking.croppedImageURL ||
                    firstConfirmedBooking.imageURL ||
                    emptyImage
                }
                alt={firstConfirmedBooking?.title || ""}
            />
            <div className="request-snippet__confirmed-icon">
                <Icon.Check.Small.P10 />
            </div>
        </div>
    ) : null;
}

function RequestBookingTitle(props: { bookings: Booking[] }) {
    const { bookings } = props;
    const firstConfirmedBooking = bookings[0];
    const suffix = bookings.length > 1 ? ` + ${bookings.length - 1} more` : "";
    return firstConfirmedBooking ? (
        <Label.Small.Normal.N10>
            {firstConfirmedBooking.title}
            {suffix}
        </Label.Small.Normal.N10>
    ) : null;
}

function RequestRating(props: SnippetProps) {
    const { request } = props;
    return request.memberFeedbackTimestamp ? (
        <Stars maxRating={5} userRating={request.memberRating ?? 0} />
    ) : null;
}

function RequestAgentAvatar(props: SnippetProps) {
    const { request } = props;
    const agentsMap = useAgentsMap();
    const membersMap = useMembersMap();
    const agentId =
        request.assignedTo && agentsMap[request.assignedTo]
            ? agentsMap[request.assignedTo].id
            : undefined;
    const member = membersMap[request.member];
    const isAssignedToMemberOwner =
        request.assignedTo !== undefined &&
        request.assignedTo === member?.assignedToAgentId;
    const agentTheMemberIsAssignedTo = member?.assignedToAgentId
        ? agentsMap[member.assignedToAgentId]
        : undefined;

    return agentId ? (
        <AgentAvatarWithSecondary
            primaryAgentId={agentId}
            secondaryAgentId={
                !isAssignedToMemberOwner ? agentTheMemberIsAssignedTo?.id : undefined
            }
        />
    ) : null;
}

function shouldShowStatus(request: Request) {
    return request.nextDeadlineStep && request.nextDeadlineAt;
}
function RequestDeadline(props: SnippetProps) {
    const { request } = props;
    return (
        <>
            {request.nextDeadlineStep && (
                <Tag.XXSmall.N90
                    disabled={requestIsActionable(request) === "non-actionable"}
                >
                    {DeadlineStepToTextMap[request.nextDeadlineStep]}
                </Tag.XXSmall.N90>
            )}
            <DeadlineTag deadlineAt={request.nextDeadlineAt} />
        </>
    );
}

function RequestStatus(props: SnippetProps) {
    const { request } = props;

    if (request.status === "new") {
        return (
            <Flex.Row gap="medium">
                <Tag.XSmall.DeadlinePrio3>New</Tag.XSmall.DeadlinePrio3>
                <TimeTag label="Added" time={fromTimestampToDate(request.createdAt)} />
            </Flex.Row>
        );
    }
    return shouldShowStatus(request) ? (
        <>
            <Divider />
            <Flex.Row gap="medium" className="request-snippet__status">
                <RequestAgentAvatar request={request} />
                <RequestDeadline request={request} />
            </Flex.Row>
        </>
    ) : null;
}

function EditButton(props: { request: Request }) {
    const { request } = props;
    const history = useHistory();

    return isMobile() ? (
        <Button
            label="Edit"
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const mobileEditLink = `/request/${request?.id}/edit`;
                history.push(mobileEditLink);
            }}
        />
    ) : null;
}

function RequestSnippetWrapper(props: RequestSnippetWrapperProps) {
    const { children } = props;
    return (
        <Flex.Column gap="medium" className="request-snippet">
            {children}
        </Flex.Column>
    );
}

function NewRequestSnippet(props: SnippetProps) {
    const { request } = props;
    return (
        <RequestSnippetWrapper>
            <Flex.Column gap="small">
                <RequestTitle request={request} />
                <RequestDetails request={request} />
            </Flex.Column>

            <RequestStatus request={request} />
            <EditButton request={request} />
        </RequestSnippetWrapper>
    );
}

function InProgressRequestSnippet(props: SnippetProps) {
    const { request } = props;
    return (
        <RequestSnippetWrapper>
            <Flex.Column gap="small">
                <RequestTitle request={request} />
                <RequestDetails request={request} />
            </Flex.Column>
            <RequestStatus request={request} />
            <EditButton request={request} />
        </RequestSnippetWrapper>
    );
}

function ConfirmedRequestSnippet(props: ConfirmedSnippetProps) {
    const { request, confirmedBookings } = props;

    return (
        <RequestSnippetWrapper>
            <Flex.Row gap="medium" alignItems="start">
                <RequestBookingImage bookings={confirmedBookings} />

                <Flex.Column gap="medium">
                    <Flex.Column gap="small">
                        <RequestTitle request={request} />
                        <RequestBookingTitle bookings={confirmedBookings} />
                        <RequestDetails request={request} />
                        <RequestRating request={request} />
                    </Flex.Column>
                </Flex.Column>
            </Flex.Row>
            <RequestStatus request={request} />
            <EditButton request={request} />
        </RequestSnippetWrapper>
    );
}

function ClosedRequestSnippet(props: ConfirmedSnippetProps) {
    const { request, confirmedBookings } = props;

    return (
        <RequestSnippetWrapper>
            <Flex.Row gap="medium" alignItems="start">
                <RequestBookingImage bookings={confirmedBookings} />

                <Flex.Column gap="medium">
                    <Flex.Column gap="small">
                        <RequestTitle request={request} />
                        <RequestBookingTitle bookings={confirmedBookings} />
                        <RequestDetails request={request} />
                        <RequestRating request={request} />
                    </Flex.Column>
                    <RequestStatus request={request} />
                    <EditButton request={request} />
                </Flex.Column>
            </Flex.Row>
        </RequestSnippetWrapper>
    );
}

function RequestSnippet(props: RequestSnippetProps) {
    const { request, confirmedBookings } = props;
    const requestSpecificConfirmedBookings = confirmedBookings
        .filter((it) => it.request === request.id)
        .flatMap((it) => it.bookings);

    if (request.status === "new") {
        return <NewRequestSnippet request={request} />;
    }

    if (request.status === "confirmed") {
        return (
            <ConfirmedRequestSnippet
                request={request}
                confirmedBookings={requestSpecificConfirmedBookings}
            />
        );
    }

    if (request.status === "closed") {
        return (
            <ClosedRequestSnippet
                request={request}
                confirmedBookings={requestSpecificConfirmedBookings}
            />
        );
    }

    return <InProgressRequestSnippet request={request} />;
}

export default RequestSnippet;
